import React, { useCallback, useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import withAuth from '../../../HOC/withAuth';
import { useTranslation } from '../../../t';
import Empty from '../../../components/Empty';
import CustomPagination from '../../../components/Pagination';
import dayjs from 'dayjs';
import InvoiceRequestDialog from '../../../components/InvoiceRequestDialog';
import InfoBar from '../InfoBar';
import C from '../../../colors';
import { formatMoney } from '../../../money';

const invoiceStatusLabels = {
  pending: '待处理',
  processing: '处理中',
  completed: '已完成',
  rejected: '已驳回',
};

const InvoiceIndex = inject('userStore')(observer(({ userStore }) => {
  const { t } = useTranslation();
  const [invoiceRequests, setInvoiceRequests] = useState([]);
  const [invoiceData, setInvoiceData] = useState({ totalPaid: 0, invoicedAmount: 0, invoicableAmount: 0 });
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);

  const loadInvoiceData = useCallback(() => Promise.all([
    userStore.fetchInvoice().then(data => setInvoiceData(data || {})),
    userStore.fetchInvoiceHistory(page).then(data => {
      const list = data.invoices || data.requests || data.history || data;
      setInvoiceRequests(Array.isArray(list) ? list : []);
      setTotalPage(data.totalPages || data.totalPage || 1);
    }),
  ]).catch(() => {}), [page, userStore]);

  useEffect(() => { loadInvoiceData(); }, [loadInvoiceData]);

  const closeInvoiceDialog = submitted => {
    setInvoiceDialogOpen(false);
    if (submitted) loadInvoiceData();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <InfoBar />
      <div style={{ flex: 1, overflow: 'auto', padding: '28px 36px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontFamily: C.serif, fontSize: 36, fontWeight: 400, color: C.ink }}>{t('Invoice Management')}</h1>
          <button data-testid="invoice-request-open" onClick={() => setInvoiceDialogOpen(true)} style={{
            fontFamily: C.sans, fontSize: 13, fontWeight: 500, padding: '9px 16px', borderRadius: 7,
            background: C.brand, color: '#fff', border: 'none', cursor: 'pointer',
            boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
          }}>{t('Request Invoice')}</button>
        </div>

        {/* KPI cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
          {[{ id: 'invoice-total-paid', l: t('Total Paid'), v: `¥${formatMoney(invoiceData.totalPaid)}` },
            { id: 'invoice-invoiced-amount', l: t('Invoiced Amount'), v: `¥${formatMoney(invoiceData.invoicedAmount)}` },
            { id: 'invoice-invoicable-amount', l: t('Invoicable Amount'), v: `¥${formatMoney(invoiceData.invoicableAmount)}`, a: true }].map(k => (
            <div data-testid={k.id} key={k.l} style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, padding: '18px 20px' }}>
              <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: C.ink3, marginBottom: 8 }}>{k.l}</div>
              <div style={{ fontFamily: C.serif, fontSize: 28, color: k.a ? C.brand2 : C.ink }}>{k.v}</div>
            </div>
          ))}
        </div>

        {invoiceRequests.length === 0 ? (
          <Empty message={t('No invoice requests')}/>
        ) : (
          <div style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: C.sans }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.hair}`, background: C.paper2 }}>
                  {[t('Request ID'), t('Invoice Type'), t('Invoice Title'), t('Amount'), t('Request Date'), t('Status')].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.ink3, fontFamily: C.mono, textTransform: 'uppercase', letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoiceRequests.map((r, i) => (
                  <tr data-testid={`invoice-record-${r.id}`} key={r.id || i} style={{ borderBottom: i < invoiceRequests.length - 1 ? `1px solid ${C.hair}` : 'none' }}>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.ink, fontFamily: C.mono }}>{r.id}</td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.ink2 }}>{t(r.type || 'personal')}</td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.ink2 }}>{r.title}</td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.ink }}>¥{formatMoney(r.amount)}</td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: C.ink2 }}>{r.createdAt ? dayjs(r.createdAt).format('YYYY-MM-DD') : '—'}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <span data-testid={`invoice-status-${r.id}`} data-status={r.status || 'pending'} style={{ padding: '3px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: C.neutralBg, color: C.neutralFg }}>
                        {invoiceStatusLabels[r.status] || r.status || invoiceStatusLabels.pending}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPage > 1 && <div style={{ marginTop: 16 }}><CustomPagination count={totalPage} page={page} onChange={(_, v) => setPage(v)}/></div>}
      </div>
      <InvoiceRequestDialog
        open={invoiceDialogOpen}
        onClose={closeInvoiceDialog}
        invoicableAmount={Number(invoiceData.invoicableAmount || 0)}
      />
    </div>
  );
}));

export default withAuth(InvoiceIndex);
